/**
 * /auth/recovery/lost-both — Tier 3 break-glass recovery.
 *
 * User has lost both their MFA device AND their email access.
 * Identity verification via knowledge-based questions + ID upload prompt.
 *
 * Wired to POST /v1/auth/recovery (anonymous pre-auth). The backend
 * env-gates behind RECOVERY_REQUESTS_ENABLED — when off, returns 503
 * RECOVERY_NOT_CONFIGURED which we surface as a clear notice.
 *
 * State: form → submitted (tracking number returned by backend, or
 * inline-error state if the API call fails).
 */

import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { AuthLayout } from '../../../components/layouts/auth-layout';
import { AuthSubmit } from '../../../components/auth/auth-controls';
import { BackToSignIn } from '../../../components/auth/back-to-sign-in';
import { lostBothSchema, type LostBothFormValues } from './lost-both.schema';
import { useCreateRecoveryRequest, ApiError } from '../../../lib/api';
import { mapError } from '../../../lib/error-messages';

export const Route = createFileRoute('/auth/recovery/lost-both')({
  component: LostBothPage,
});

const EMPTY: LostBothFormValues = {
  fullName: '',
  idNumber: '',
  employerOrFirm: '',
  lastKnownEmail: '',
  mothersMaidenName: '',
  yearOfBirth: '',
  additionalContext: '',
};

function validate(values: LostBothFormValues) {
  const result = lostBothSchema.safeParse(values);
  if (result.success) return {} as Record<string, string>;
  const errs: Record<string, string> = {};
  for (const issue of result.error.issues) {
    const key = issue.path[0] as string;
    if (!errs[key]) errs[key] = issue.message;
  }
  return errs;
}

function LostBothPage() {
  const [values, setValues] = useState<LostBothFormValues>(EMPTY);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submittedTrackingRef, setSubmittedTrackingRef] = useState<string | null>(null);
  const [submittedAt, setSubmittedAt] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const createMutation = useCreateRecoveryRequest();

  const errors = validate(values);
  const isValid = Object.keys(errors).length === 0;

  function set(field: keyof LostBothFormValues, val: string) {
    setValues((prev) => ({ ...prev, [field]: val }));
  }
  function touch(field: string) {
    setTouched((prev) => ({ ...prev, [field]: true }));
  }

  function handleSubmit() {
    const allTouched: Record<string, boolean> = {};
    for (const k of Object.keys(EMPTY)) allTouched[k] = true;
    setTouched(allTouched);
    if (!isValid) return;

    setSubmitError(null);
    createMutation.mutate(
      {
        email: values.lastKnownEmail,
        tier: 3,
        reason: values.additionalContext?.trim() || 'Tier-3 lost-both recovery: see KBA answers.',
        kbaAnswers: {
          fullName: values.fullName,
          idNumber: values.idNumber,
          employerOrFirm: values.employerOrFirm,
          mothersMaidenName: values.mothersMaidenName,
          yearOfBirth: values.yearOfBirth,
        },
      },
      {
        onSuccess: (res) => {
          setSubmittedTrackingRef(res.recoveryRequestId);
          setSubmittedAt(
            new Date().toLocaleDateString('en-ZA', {
              day: 'numeric',
              month: 'short',
              year: 'numeric',
            }) +
              ' · ' +
              new Date().toLocaleTimeString('en-ZA', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
              }) +
              ' SAST',
          );
        },
        onError: (err: ApiError) => {
          if (err.status === 503 && err.code === 'RECOVERY_NOT_CONFIGURED') {
            setSubmitError(
              "Recovery requests aren't enabled for this environment yet. " +
                'Please contact support@elreign.io directly while we finish provisioning.',
            );
            return;
          }
          setSubmitError(mapError(err, 'submit-request'));
        },
      },
    );
  }

  if (submittedTrackingRef) {
    return (
      <AuthLayout
        eyebrow="Recovery submitted"
        heading="Request received."
        sub="A Tier 3 superadmin review has been opened. No access is granted until the review is complete."
        stepUp
      >
        {/* Tracking card */}
        <div
          style={{
            background: 'var(--surface)',
            border: '1px solid var(--hairline-2)',
            borderRadius: 12,
            padding: '20px 22px',
            marginBottom: 20,
          }}
        >
          <div className="eyebrow" style={{ marginBottom: 8 }}>Your tracking reference</div>
          <div
            className="mono"
            style={{
              fontSize: 16,
              fontWeight: 700,
              color: 'var(--ink)',
              letterSpacing: '0.04em',
              wordBreak: 'break-all',
            }}
          >
            {submittedTrackingRef}
          </div>
          <div style={{ marginTop: 10, fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.55 }}>
            Keep this reference. Elreign support will contact you at the email address you
            provided to arrange out-of-band identity verification. Expected response time is
            1-3 business days.
          </div>
        </div>

        {/* Status steps */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {[
            { label: 'Request received', done: true },
            { label: 'Identity verification (out of band)', done: false },
            { label: 'Superadmin review', done: false },
            { label: 'Access restored', done: false },
          ].map((step, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 14,
                padding: '10px 0',
                borderBottom: i < 3 ? '1px solid var(--hairline)' : 'none',
              }}
            >
              <span
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  border: `2px solid ${step.done ? 'var(--positive)' : 'var(--hairline-2)'}`,
                  background: step.done ? 'var(--positive)' : 'transparent',
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                  marginTop: 1,
                }}
              >
                {step.done && (
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <path d="M2 5l2.5 2.5L8 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </span>
              <span style={{ fontSize: 13.5, color: step.done ? 'var(--ink)' : 'var(--ink-3)', fontWeight: step.done ? 500 : 400 }}>
                {step.label}
              </span>
            </div>
          ))}
        </div>

        <p style={{ marginTop: 20, fontSize: 12, color: 'var(--ink-4)', lineHeight: 1.55, textAlign: 'center' }}>
          Submitted {submittedAt}
        </p>

        {/* DESIGN GATE B2: somebody told nothing happens for days still needs
            a way off this screen that is not the browser's back button. */}
        <BackToSignIn />
      </AuthLayout>
    );
  }

  // ── Form state ────────────────────────────────────────────────────────────────

  const fields: Array<{
    id: keyof LostBothFormValues;
    label: string;
    placeholder: string;
    type?: string;
  }> = [
    { id: 'fullName',         label: 'Full legal name',              placeholder: 'As it appears on your ID document' },
    { id: 'idNumber',         label: 'ID or passport number',        placeholder: 'South African ID or passport number' },
    { id: 'lastKnownEmail',   label: 'Last known email address',     placeholder: 'The email registered to your account', type: 'email' },
    { id: 'employerOrFirm',   label: 'Employer or law firm',         placeholder: 'Company or firm name' },
    { id: 'mothersMaidenName',label: "Mother's maiden name",         placeholder: 'Knowledge-based verification' },
    { id: 'yearOfBirth',      label: 'Year of birth',                placeholder: 'e.g. 1982' },
  ];

  return (
    <AuthLayout
      eyebrow="Tier 3 recovery"
      heading="Identity verification required."
      sub="You have indicated that you have lost access to both your email and MFA device. This request will be reviewed by a Tier 3 superadmin. Expect 1-3 business days."
      stepUp
    >
      <div
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--accent-300)',
          borderRadius: 10,
          padding: '14px 16px',
          marginBottom: 20,
          fontSize: 13,
          color: 'var(--accent-700)',
          lineHeight: 1.55,
        }}
      >
        All information you provide is logged and subject to audit. Providing false information may constitute fraud under South African law.
      </div>

      {submitError && (
        <div
          role="alert"
          style={{
            background: 'var(--negative-soft)',
            border: '1px solid var(--negative)',
            color: 'var(--negative)',
            padding: '12px 14px',
            borderRadius: 10,
            marginBottom: 18,
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          {submitError}
        </div>
      )}

      {/* EVERY LABEL NAMES ITS FIELD (2026-09-22). Until then no label on this
          form carried `htmlFor` and no input an `id`, so a screen reader
          announced six unnamed text boxes and an unnamed text area on the one
          form a person locked out of both email and authenticator must fill in.
          Same pattern as /auth/forgot, /auth/recovery and /auth/recovery/status:
          `htmlFor` on the label, the same stable id on the control, and the
          error line tied to its field by `aria-describedby`. The ids come from
          the field key, so they do not move between renders. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {fields.map(({ id, label, placeholder, type }) => {
          const err = touched[id] ? errors[id] : undefined;
          const inputId = `lost-both-${id}`;
          const errorId = `${inputId}-error`;
          return (
            <div key={id} className="field">
              <label className="field-label" htmlFor={inputId}>{label}</label>
              <input
                id={inputId}
                type={type ?? 'text'}
                className="field-input"
                placeholder={placeholder}
                value={values[id] as string}
                onChange={(e) => set(id, e.target.value)}
                onBlur={() => touch(id)}
                aria-invalid={err !== undefined}
                aria-describedby={err !== undefined ? errorId : undefined}
                style={err ? { borderColor: 'var(--negative)', outline: 'none' } : undefined}
              />
              {err && (
                <div id={errorId} style={{ marginTop: 4, fontSize: 12, color: 'var(--negative)' }}>{err}</div>
              )}
            </div>
          );
        })}

        {/* ID document upload — visual stub (real evidence upload lands when
            the documents/upload-init flow is wired into this pre-auth path). */}
        <div className="field">
          {/* Not a <label>: there is no control here to label yet, only a
              disabled stub, and a label pointing at nothing is announced as a
              field that is not there. The stub takes its name from this line
              instead. `.field-label` is a class rule, so it looks the same. */}
          <div className="field-label" id="lost-both-id-document-label">ID document (copy)</div>
          <div
            role="group"
            aria-labelledby="lost-both-id-document-label"
            aria-disabled="true"
            style={{
              border: '1.5px dashed var(--hairline-2)',
              borderRadius: 8,
              padding: '18px 16px',
              textAlign: 'center',
              background: 'var(--surface)',
              cursor: 'not-allowed',
              opacity: 0.65,
            }}
          >
            <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>Upload connects in a follow-up</div>
            <div style={{ fontSize: 11.5, color: 'var(--ink-4)', marginTop: 4 }}>
              PDF, JPG, or PNG · max 5 MB. For now, bring the ID copy to the out-of-band call.
            </div>
          </div>
        </div>

        {/* Additional context */}
        <div className="field">
          <label className="field-label" htmlFor="lost-both-additionalContext">
            Additional context (optional)
          </label>
          <textarea
            id="lost-both-additionalContext"
            className="field-input"
            placeholder="Any additional information that may help verify your identity"
            rows={3}
            value={values.additionalContext ?? ''}
            onChange={(e) => set('additionalContext', e.target.value)}
            style={{ resize: 'vertical', fontFamily: 'var(--sans)' }}
          />
        </div>
      </div>

      <div style={{ marginTop: 20 }}>
        <AuthSubmit
          type="button"
          onClick={handleSubmit}
          label="Submit recovery request"
          busyLabel="Submitting…"
          busy={createMutation.isPending}
        />
      </div>

      <p style={{ marginTop: 16, fontSize: 12, color: 'var(--ink-4)', lineHeight: 1.55, textAlign: 'center' }}>
        You will receive a tracking number on submission. Keep it safe: you will need it when Elreign support contacts you.
      </p>

      {/* DESIGN GATE B2: no link on this form led anywhere but the logo. No
          lead-in, because none is true for somebody who has lost both. */}
      <BackToSignIn />
    </AuthLayout>
  );
}
